import { useCallback, useMemo, useRef, useState } from 'react';
import { getUserCompanies, selectCompany } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { activateDefaultSite, getDefaultSite } from '../utils/defaultSite';
import { getSubdomain, buildTenantUrl, buildMainDomainUrl, resolveTenant } from '@shared/lib/tenant';
import { getFirmName } from '@shared/utils/firmName';
import { showToast } from '@shared/services/toast';
import { clearReviewOrderCopy } from '@shared/utils/reviewOrderStorage';

/**
 * In-app "switch company" for a signed-in user, rendered as the company card at the top of the sidebar
 * (`AppSidebar`). Self-contained so it does NOT touch the login bootstrap
 * (`useTenantBootstrap`/`KeycloakProvider`).
 *
 * The firm list is the same one the bootstrap uses — `GET /api/firm` via `getUserCompanies()` — lazily
 * fetched the first time the dropdown opens (`loadCompanies`). Switching sets the backend active-firm
 * cookie via `selectCompany(firmId)` and the active-site cookie to the picked site, or that firm's
 * default site (`activateDefaultSite`), then reloads (or hands off to another subdomain) so ALL page data
 * is cleared and re-hydrated for the newly selected firm. Load/switch failures surface via
 * `showToast.error`.
 *
 * @returns {{
 *   companies: Array<{firmId:number,name:string,subDomain?:string,idpHint?:string}>,
 *   loading: boolean, switching: boolean, currentFirmId: number|null,
 *   loadCompanies: () => Promise<void>,
 *   switchCompany: (company: object, site?: {haloPsaSiteId:number}) => Promise<void>
 * }}
 */
export function useCompanySwitcher() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const loadedRef = useRef(false);

  const selectedCompany = useAuthStore((s) => s.selectedCompany);
  const setSelectedCompany = useAuthStore((s) => s.setSelectedCompany);
  const setSelectedSite = useAuthStore((s) => s.setSelectedSite);
  const setCompanyCount = useAuthStore((s) => s.setCompanyCount);
  const profile = useUserStore((s) => s.profile);

  // The firm currently in context — used to mark the active row and short-circuit a no-op switch.
  const currentFirmId =
    selectedCompany?.firmId ??
    profile?.firm?.firmId ??
    profile?.firmId ??
    companies.find((c) => c.isCurrent)?.firmId ??
    null;

  // The list is fetched once per session, so a rename saved from the profile would otherwise only
  // reach the dropdown after a reload. The profile store already holds the saved name (it is what the
  // card title shows), so the active firm's row takes its name from there. Only the active firm can be
  // renamed from the profile, so the other rows are left as fetched.
  const activeFirmName = getFirmName(profile);
  const companiesView = useMemo(
    () =>
      activeFirmName
        ? companies.map((c) => (c.firmId === currentFirmId ? { ...c, name: activeFirmName } : c))
        : companies,
    [companies, currentFirmId, activeFirmName],
  );

  // Fetch the user's firms once, on first dropdown open. Repeated calls are no-ops.
  const loadCompanies = useCallback(async () => {
    if (loadedRef.current || loading) return;
    setLoading(true);
    try {
      const list = await getUserCompanies();
      const safeList = Array.isArray(list) ? list : [];
      setCompanies(safeList);
      // Shared with AppHeader, which shows the company label only for multi-firm users. Set on success
      // only, so a failed fetch leaves it null and the header shows no company rather than guessing.
      setCompanyCount(safeList.length);
      loadedRef.current = true;
    } catch (e) {
      console.error('[Company Switch] Failed to load companies:', e);
      showToast.error('Failed to load companies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, setCompanyCount]);

  const activateSiteForCompany = useCallback(
    async (company, site) => {
      try {
        await activateDefaultSite(company, site);
        setSelectedSite(site ?? getDefaultSite(company.sites));
      } catch (siteError) {
        // The firm cookie already points at the new firm; leaving it there with the old firm's site
        // would mix the two. Best-effort put the previous firm back, then surface the failure.
        if (currentFirmId != null) {
          await selectCompany(currentFirmId).catch((rollbackError) => {
            // Best-effort only: the original site failure is what the user needs to see, so a failed
            // rollback is logged and must not mask it.
            console.warn('[Company Switch] Failed to restore the previous firm after a site error:', rollbackError);
          });
        }
        throw siteError;
      }
    },
    [currentFirmId, setSelectedSite],
  );

  // When the target firm lives on a different host, hand the session off there and return true.
  // Returns false when the caller should fall through to a same-host reload.
  const handleCrossHostHandoff = useCallback(async (company) => {
    const currentSub = getSubdomain();
    const currentSubNorm = currentSub ? currentSub.toLowerCase() : null;
    const targetSub = company.subDomain ? company.subDomain.toLowerCase() : null;

    if (targetSub === currentSubNorm) return false;

    if (!targetSub) {
      // Target is a main-domain firm but we're on a company subdomain — go to the main host.
      globalThis.location.href = buildMainDomainUrl('/');
      return true;
    }

    const target = buildTenantUrl(company.subDomain, '/');
    // Unsafe/malformed subDomain — fall through to a same-host reload rather than navigate
    // somewhere unexpected (buildTenantUrl already rejected it).
    if (!target) return false;

    // Best-effort: refresh tenant_sub/idp_alias before leaving. A failure must not block.
    try {
      await resolveTenant(company.subDomain);
    } catch (e) {
      console.warn('[Company Switch] Tenant resolve failed before subdomain redirect:', e);
    }
    globalThis.location.href = target;
    return true;
  }, []);

  const switchCompany = useCallback(
    async (company, site) => {
      // Ignore a click on the already-active firm, an invalid entry, or a switch already in flight.
      if (!company || company.firmId === currentFirmId || switching) return;

      setSwitching(true);
      try {
        // Backend re-issues the HTTP-only `firm_id` cookie for the new firm (source of truth) and
        // refreshes sidebar visibility.
        await selectCompany(company.firmId);
        await activateSiteForCompany(company, site);
        // Keep localStorage's copy consistent so authStore reflects the new firm after the reload.
        setSelectedCompany(company);
        // An order started under the previous firm/site must not be revived under this one.
        clearReviewOrderCopy();

        const handedOff = await handleCrossHostHandoff(company);
        if (handedOff) return;

        // Same host — navigate to the home route. A full navigation clears every in-memory store and
        // re-hydrates profile/roles/sidebar for the new active firm (no cross-firm bleed), and landing
        // on "/" lets HomeRedirect pick the firm's lifecycle-appropriate page — dashboard, or checkout
        // for a firm with no invoices yet — instead of reloading onto a now-gated page that renders
        // blank. Matches the cross-subdomain paths above, which already hand off to the target root.
        globalThis.location.href = '/';
      } catch (e) {
        console.error('[Company Switch] Failed to switch company:', e);
        showToast.error(e?.response?.data?.message || e?.message || 'Failed to switch company. Please try again.');
        setSwitching(false);
      }
    },
    [currentFirmId, switching, setSelectedCompany, activateSiteForCompany, handleCrossHostHandoff],
  );

  return { companies: companiesView, loading, switching, currentFirmId, loadCompanies, switchCompany };
}
